/**
 * deviceUUID устройства для Mindbox.
 *
 * С Mindbox общается только наш бэкенд — сами мы туда ничего не отправляем.
 * Фронт лишь передаёт бэкенду deviceUUID (`authAPI`, `getMindboxUserInfo`),
 * чтобы его серверные операции попадали на правильное устройство.
 *
 * Источники, по убыванию приоритета:
 *   1. localStorage — ключ кладёт мобильное приложение, открывая мини-апп в
 *      webview: так web и приложение считаются одним устройством.
 *   2. Бэкенд — присылает UUID вместе с профилем в `/user/me`
 *      (см. `setMindboxDeviceUUID`).
 */
const DEVICE_UUID_KEY = 'mindboxDeviceUUID';

// Сколько ждём UUID из `/user/me`, прежде чем отказаться от запроса.
const WAIT_TIMEOUT_MS = 5000;

let deviceUUID: string | undefined;
const waiters = new Set<(uuid: string) => void>();

const readStoredDeviceUUID = () => {
  try {
    const stored = localStorage.getItem(DEVICE_UUID_KEY);
    return stored ? decodeURIComponent(stored) : undefined;
  } catch {
    // Приватный режим: localStorage недоступен.
    return undefined;
  }
};

/** UUID, если он уже известен, — не дожидаясь `/user/me`. */
export const getMindboxDeviceUUID = (): Promise<string | undefined> => {
  deviceUUID ??= readStoredDeviceUUID();
  return Promise.resolve(deviceUUID);
};

const generateUUID = () => {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();

  // Старые webview без randomUUID: v4 из getRandomValues.
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map(byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * UUID для входа через Telegram. `/user/me` грузится только после входа,
 * поэтому при первом входе UUID ещё неоткуда взять, и бэкенд звал
 * Online.Authorization без него («DeviceUUID is missing»). Тогда создаём UUID
 * сами и сохраняем — следующие открытия мини-аппа будут тем же устройством.
 */
export const ensureMindboxDeviceUUID = async (): Promise<string> => {
  const known = await getMindboxDeviceUUID();
  if (known) return known;

  const uuid = generateUUID();
  try {
    localStorage.setItem(DEVICE_UUID_KEY, uuid);
  } catch {
    // Приватный режим: живём с UUID до конца сессии.
  }
  setMindboxDeviceUUID(uuid);
  return uuid;
};

/**
 * UUID с ожиданием `/user/me`: событие, случившееся до загрузки профиля, не
 * теряется, а уходит, как только бэкенд пришлёт UUID. Не дождались за
 * WAIT_TIMEOUT_MS — undefined, запрос не отправляем.
 *
 * Не для входа: `/user/me` грузится только после него, и вход бы просто
 * простоял весь таймаут.
 */
export const waitForMindboxDeviceUUID = async (): Promise<string | undefined> => {
  const known = await getMindboxDeviceUUID();
  if (known) return known;

  return new Promise(resolve => {
    const onReady = (uuid: string) => {
      clearTimeout(timer);
      resolve(uuid);
    };
    const timer = setTimeout(() => {
      waiters.delete(onReady);
      resolve(undefined);
    }, WAIT_TIMEOUT_MS);
    waiters.add(onReady);
  });
};

/**
 * deviceUUID, который прислал бэкенд вместе с профилем (`/user/me`).
 * Первый известный UUID выигрывает: менять устройство посреди сессии нельзя.
 */
export const setMindboxDeviceUUID = (uuid?: string) => {
  if (!uuid || deviceUUID) return;
  deviceUUID = uuid;

  waiters.forEach(resolve => resolve(uuid));
  waiters.clear();
};
